import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, StateWord, SummaryStripBase } from "@/design/primitives";
import { DetailOverlay } from "@/design/primitives/DetailOverlay";
import { NoteOnCard } from "@/design/primitives/NoteOnCard";
import { ItemEditorBar } from "@/design/primitives/ItemEditorBar";
import {
  ARCHIVED_LANE,
  LANE_CHIP,
  LANE_LABEL,
  LANE_ORDER,
  MEASURES,
  MEASURE_WORD,
  OTHER_LANE,
  areaCounts,
  boostOf,
  boostReasonOf,
  buildLanes,
  completedBreakdown,
  completedSummary,
  laneOf,
  matchesFilters,
  measuresDone,
  measuresTotal,
  nextUp,
  nextUpList,
  phaseIndexOf,
  phaseLabelOf,
  phaseOf,
  phaseToneOf,
} from "./citiesModel";

// TJB City Projects board (redesigned 2026-10-22 for Jeff's four complaints).
//
// The board is a LANE board, not a card wall. It answers, top to bottom:
//   1. "Which city page is up for being worked on?" → the WORKING lane is
//      pinned to the top and ALWAYS renders — even at zero, where it says
//      so in plain words instead of going silent. When Kit has nothing in
//      flight, the "next up" card names the city that is up for the work.
//   2. "Where are the completed ones?" → the COMPLETED lane is a summary
//      (count, the last few months, the newest finish) with the full list
//      one click behind it. Never a wall of 176 cards.
//   3. "Where is the filtering?" → search, US state, "missing this part",
//      and a lane picker that says exactly what is on screen.
//   4. "It takes up too much space" → the 1,051-city queue is a compact
//      prioritized LIST (not a card wall), the finished list is a summary,
//      and the density control tightens the cards.
//
// The card contract with the ingest side is unchanged: every lane is
// DERIVED from the card's own fields (see citiesModel.js). The producer
// keeps stamping exactly what it stamps today.

const DENSITY = {
  comfortable: { pad: "px-5 py-4", gap: "gap-3", title: "text-[1.0625rem]", showMeasures: true },
  compact: { pad: "px-4 py-2.5", gap: "gap-1.5", title: "text-[0.9375rem]", showMeasures: false },
};

// The queue holds 1,051 cities. A page of 25 keeps the whole lane inside
// one screenful — the rest is one click away ("show 25 more of 1,051").
const QUEUE_PAGE = 25;

// "All" plus the five lanes — a radio, not a pile of toggles. One lane on
// screen at a time, except that the working lane is a STATUS and never
// disappears (see WorkingNow).
const LANE_FILTERS = ["all", ...LANE_ORDER, ARCHIVED_LANE];

const LaneFilter = ({ lane, count, active, onClick }) => (
  <button
    type="button"
    data-testid={`cities-chip-${lane}`}
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[0.6875rem] transition-colors",
      active
        ? "border-os-border-strong bg-os-ink text-white"
        : "border-os-border text-os-ink-2 hover:border-os-border-strong hover:text-os-ink",
    )}
  >
    {lane === "all" ? "All" : LANE_CHIP[lane]}
    <span className="tabular-nums">{count}</span>
  </button>
);

// The working count is a STATUS, not a filter — the one thing Jeff asked to
// see never gets hidden behind a toggle.
const WorkingStatus = ({ count }) => (
  <span
    data-testid="cities-working-status"
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[0.6875rem]",
      count > 0 ? "border-os-green/50 bg-os-green/10 text-os-green" : "border-os-border text-os-ink-3",
    )}
  >
    {LANE_CHIP.working}
    <span className="tabular-nums">{count}</span>
  </span>
);

const MeasureChips = ({ measures, slug, size = "sm" }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid={`cities-measures-${slug}`}>
    {MEASURES.map(({ key }) => (
      <span
        key={key}
        title={`${MEASURE_WORD[key]}: ${measures?.[key] ? "done" : "not yet"}`}
        data-testid={`cities-measure-${slug}-${key}-${measures?.[key] ? "done" : "todo"}`}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-1.5 font-mono uppercase tracking-[0.04em]",
          size === "sm" ? "py-0.5 text-[0.5625rem]" : "px-2 py-0.5 text-[0.6875rem]",
          measures?.[key]
            ? "border-os-green/40 bg-os-green/10 text-os-green"
            : "border-os-border bg-transparent text-os-ink-4",
        )}
      >
        <span className={cn("inline-block h-1.5 w-1.5 rounded-full", measures?.[key] ? "bg-os-green" : "bg-os-ink-4/40")} />
        {MEASURE_WORD[key].split(" ")[0]}
      </span>
    ))}
  </div>
);

// ── The phase chip (Jeff directive 2026-10-23): where this city sits in
//    the 4-phase DAG — build → enrich → verify deploy → video+outreach,
//    plus done. The chip says the phase in one word, the way the board
//    says everything else: plainly.
//
//    `step` (the phase's number) is carried for the reader and for the
//    tests; it is not drawn as a progress bar (the design tokens forbid
//    fake progress bars).
const PHASE_CHIP_TONE = {
  amber: "border-os-amber/40 bg-os-amber/10 text-os-amber",
  green: "border-os-green/40 bg-os-green/10 text-os-green",
  red: "border-os-red/40 bg-os-red/10 text-os-red",
};

const PhaseChip = ({ item, size = "sm" }) => {
  const phase = phaseOf(item);
  const label = phaseLabelOf(item);
  const tone = phaseToneOf(item);
  const step = phaseIndexOf(item);
  return (
    <span
      data-testid={`cities-phase-${item?.slug}`}
      data-phase={phase}
      data-phase-index={step}
      title={`Phase ${step + 1} of 4 — ${label}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-mono uppercase tracking-[0.06em]",
        size === "sm" ? "px-1.5 py-0.5 text-[0.5625rem]" : "px-2 py-0.5 text-[0.6875rem]",
        PHASE_CHIP_TONE[tone] || PHASE_CHIP_TONE.amber,
      )}
    >
      {label}
    </span>
  );
};

// ── The boost marker: a city a multi-market provider made worth doing
//    first. Small, quiet, and it says why on hover.
const BoostMarker = ({ item }) => {
  const boost = boostOf(item);
  if (!boost) return null;
  const reason = boostReasonOf(item);
  return (
    <span
      data-testid={`cities-boost-${item?.slug}`}
      data-boost={boost}
      title={reason ? `Boosted — ${reason}` : `Boosted — moved up the queue`}
      className="inline-flex items-center gap-1 rounded-full border border-os-bronze bg-os-gold-tint px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.06em] text-os-amber"
    >
      ▲
      {reason ? "Boosted" : `+${boost}`}
    </span>
  );
};

// ── The working-now card. The loudest thing on the board: green keyline,
//    a "Kit is on it" word, and what he is doing.
// `canOpen` is false in baked (signed-out) mode: there is no live row to
// open, so the card must not pretend to be clickable.
const WorkingCard = ({ item, onOpen, canOpen = true }) => {
  const p = item.payload || {};
  return (
    <Card
      data-testid={`cities-card-${item.slug}`}
      data-state={item.state}
      data-lane="working"
      onClick={canOpen ? onOpen : undefined}
      className={cn(
        "group flex flex-col gap-2 border-os-green px-5 py-4 transition-colors",
        canOpen && "cursor-pointer hover:border-os-green",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 text-[1.0625rem] font-semibold leading-tight tracking-[-0.02em] text-os-ink">
          {p.title}
        </h3>
        <StateWord tone="green">Kit is on it</StateWord>
      </div>
      {p.kitNow ? (
        <p data-testid={`cities-card-kit-${item.slug}`} className="text-[0.875rem] leading-[1.45] text-os-ink-2">
          {p.kitNow}
        </p>
      ) : (
        <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
          Kit has this city open and is working on it now.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <PhaseChip item={item} />
        <BoostMarker item={item} />
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <MonoMeta>{measuresDone(item)}/{measuresTotal(item)} parts done</MonoMeta>
        <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-green">
          ● live — Kit on it
        </span>
      </div>
    </Card>
  );
};

// ── The ordinary card (in build / blocked).
const CityCard = ({ item, onOpen, density, canOpen = true }) => {
  const p = item.payload || {};
  const {
    title, stateWord, stateTone, progressLine, measures, measuresDone: done,
    measuresTotal: total, score, blockedReason,
  } = p;
  const lane = laneOf(item);
  const d = DENSITY[density] || DENSITY.comfortable;
  return (
    <Card
      data-testid={`cities-card-${item.slug}`}
      data-state={item.state}
      data-lane={lane}
      onClick={canOpen ? onOpen : undefined}
      className={cn(
        "group flex flex-col transition-colors",
        canOpen && "cursor-pointer hover:border-os-border-strong",
        d.pad,
        d.gap,
        lane === "blocked" && "border-os-red",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className={cn("min-w-0 font-semibold leading-tight tracking-[-0.02em] text-os-ink", d.title)}>
          {title}
        </h3>
        <div className="flex shrink-0 items-center gap-2">
          {typeof p.priorityRank === "number" && (
            <span
              data-testid={`cities-card-rank-${item.slug}`}
              className="rounded-full border border-os-border px-2 py-0.5 font-mono text-[0.625rem] tracking-[0.04em] text-os-ink-3"
              title={`Build priority #${p.priorityRank} — high-priority cities first`}
            >
              #{p.priorityRank}
            </span>
          )}
          <StateWord tone={stateTone}>{stateWord}</StateWord>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <PhaseChip item={item} />
        <BoostMarker item={item} />
      </div>
      <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">{progressLine}</p>
      {lane === "blocked" && blockedReason && (
        // Clamped: one city lists six missing contacts — the full reason
        // lives in the detail overlay, not in a card that would stretch
        // the whole lane.
        <p className="line-clamp-2 text-[0.8125rem] leading-[1.45] text-os-red">{blockedReason}</p>
      )}
      {d.showMeasures && <MeasureChips measures={measures} slug={item.slug} />}
      <div className="mt-1 flex items-center justify-between gap-2">
        <MonoMeta data-testid={`cities-card-progress-${item.slug}`}>
          {done ?? measuresDone(item)}/{total ?? measuresTotal(item)} parts done
          {lane !== "done" && typeof score === "number" && score > 0 ? ` · score ${score}` : ""}
        </MonoMeta>
      </div>
    </Card>
  );
};

// ── A queued city: a line, not a card. 1,051 of these must not take up
//    a screenful each.
const QueueRow = ({ item, onOpen, canOpen = true }) => {
  const p = item.payload || {};
  const pop = p.population ? Number(p.population).toLocaleString("en-US") : null;
  return (
    <li className="border-b border-os-border last:border-b-0">
      <button
        type="button"
        data-testid={`cities-queue-row-${item.slug}`}
        onClick={canOpen ? onOpen : undefined}
        className={cn(
          "flex w-full items-baseline gap-3 py-2 text-left",
          canOpen && "hover:underline hover:underline-offset-2",
        )}
      >
        <span className="w-11 shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-4">
          {typeof p.priorityRank === "number" ? `#${p.priorityRank}` : "—"}
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink">{p.title}</span>
        {pop && <span className="hidden shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-4 sm:inline">{pop}</span>}
        <span className="shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-3">
          {measuresDone(item)}/{measuresTotal(item)}
        </span>
      </button>
    </li>
  );
};

// ── One finished city, listed (not a card wall).
const DoneRow = ({ item, onOpen, canOpen = true }) => {
  const p = item.payload || {};
  const completed = p.completedAt;
  const when = completed
    ? new Date(completed).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  return (
    <li className="border-b border-os-border last:border-b-0">
      <button
        type="button"
        data-testid={`cities-done-row-${item.slug}`}
        onClick={canOpen ? onOpen : undefined}
        className={cn(
          "flex w-full items-baseline justify-between gap-3 py-2 text-left",
          canOpen && "hover:underline hover:underline-offset-2",
        )}
      >
        <span className="min-w-0 truncate text-[0.875rem] text-os-ink">{p.title}</span>
        <span className="shrink-0 font-mono text-[0.6875rem] text-os-ink-3">{when || "no date"}</span>
      </button>
    </li>
  );
};

// ── A lane whose contents fold away. Collapsed = a one-line header with
//    the count and the name of the first city, so nothing disappears.
const Lane = ({ lane, items, onOpen, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const head = items[0]?.payload?.title;
  return (
    <section data-testid={`cities-lane-${lane}`} data-open={open ? "true" : "false"} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button
          type="button"
          data-testid={`cities-toggle-${lane}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex min-h-[1.75rem] items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-4 hover:text-os-ink"
        >
          <span className={cn("inline-block transition-transform", open && "rotate-90")}>▶</span>
          {LANE_LABEL[lane]}
        </button>
        <span className="font-mono text-[0.6875rem] tabular-nums text-os-ink-3">{items.length}</span>
        {!open && head && (
          <MonoMeta className="min-w-0 truncate text-os-ink-3">first — {head}</MonoMeta>
        )}
      </div>
      {open && children}
    </section>
  );
};

// ── The completed section: a summary by default, the full list on request.
const CompletedLane = ({ doneItems, onOpen, showingAll, onShowAll, canOpen }) => {
  const summary = useMemo(() => completedSummary(doneItems), [doneItems]);
  const breakdown = useMemo(() => completedBreakdown(summary), [summary]);
  const newest = summary.newest?.payload?.title;
  // The newest finish, as a real date — not a month bucket.
  const newestWhen = summary.newest?.payload?.completedAt
    ? new Date(summary.newest.payload.completedAt).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      })
    : null;
  return (
    <section data-testid="cities-lane-done" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-4">
          Completed — {summary.total}
        </h2>
        <MonoMeta className="text-os-ink-3">{breakdown}</MonoMeta>
        {newest && <MonoMeta className="text-os-ink-3">newest — {newest}</MonoMeta>}
        <button
          type="button"
          data-testid="cities-toggle-done"
          aria-expanded={showingAll}
          onClick={onShowAll}
          className="ml-auto min-h-[1.75rem] shrink-0 font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
        >
          {showingAll ? "hide the list" : "show all the finished cities"}
        </button>
      </div>
      {showingAll ? (
        <ul data-testid="cities-done-list" className="rounded-card border border-os-border bg-white px-4">
          {doneItems.map((item) => (
            <DoneRow key={item.slug} item={item} onOpen={onOpen} canOpen={canOpen} />
          ))}
        </ul>
      ) : (
        <Card quiet className="px-5 py-3">
          <p data-testid="cities-done-summary" className="text-[0.875rem] leading-[1.45] text-os-ink-2">
            {summary.total} city pages are finished and live
            {newestWhen ? ` — the newest, ${newestWhen}, is ${newest}` : ""}.{" "}
            The list is one click away — no wall of cards.
          </p>
        </Card>
      )}
    </section>
  );
};

const SummaryBlock = ({ total, lanes, meta, live, desktop }) => {
  const asOf = meta?.as_of || meta?.ingested_at || "unknown";
  const working = lanes.working.length;
  const blocked = lanes.blocked.length;
  const building = lanes.building.length;
  const queued = lanes.queued.length;
  const done = lanes.done.length;
  // Jeff 2026-10-23: the strip must hold ONE line — labels shortened to their
  // tense: "with Kit", "blocked", "in build", "queued", "done". The aria-label
  // keeps the full sentences for screen readers.
  const parts = [
    { key: "working", text: working ? `${working} with Kit` : "none with Kit", tone: working ? "green" : "neutral" },
    { key: "blocked", text: blocked ? `${blocked} blocked` : "none blocked", tone: blocked ? "red" : "neutral" },
    { key: "building", text: `${building} in build`, tone: building ? "amber" : "neutral" },
    { key: "queued", text: `${queued} queued`, tone: "neutral" },
    { key: "done", text: `${done} done`, tone: "green" },
  ];
  return (
    <SummaryStripBase
      parts={parts}
      text={`${total} city pages tracked — ${working} with Kit right now, ${blocked} blocked, ${building} in build, ${queued} queued, ${done} completed`}
      testId="cities-summary"
      size={desktop ? "md" : "sm"} // lg wrapped to 2 lines at 1,261 cities (Jeff 10/23); md = 1.375rem holds one line
      meta={
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <MonoMeta as="p" data-testid="cities-stamp" className="whitespace-nowrap pt-1">Current as of {asOf}</MonoMeta>
          {!live && <MonoMeta as="p" className="whitespace-nowrap">last saved copy</MonoMeta>}
        </div>
      }
    />
  );
};

// ── The next-up strip (Jeff directive 2026-10-23): the compact answer to
//    "what should Kit do next?". Five rows — rank chip, city, phase, and
//    the boost reason when a multi-market provider moved it up.
//
//    It folds away (the board is dense by design) and says so plainly when
//    there is nothing to do.
const NextUpStrip = ({ rows, onOpen, canOpen = true }) => {
  const [open, setOpen] = useState(true);
  if (!rows.length) {
    return (
      <Card as="section" data-testid="cities-next-up-strip" data-open="false" className="px-5 py-3.5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Label as="h2">Next up</Label>
          <p data-testid="cities-next-up-empty" className="text-[0.875rem] leading-[1.45] text-os-ink-2">
            Nothing is waiting to be built — every city page is either done, blocked, or already being worked on.
          </p>
        </div>
      </Card>
    );
  }
  return (
    <Card as="section" data-testid="cities-next-up-strip" data-open={open ? "true" : "false"} className="px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button
          type="button"
          data-testid="cities-next-up-strip-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="min-h-[1.75rem] font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-4 hover:text-os-ink"
        >
          <span className={cn("mr-1.5 inline-block transition-transform", open && "rotate-90")}>▶</span>
          Next up
        </button>
        <MonoMeta className="text-os-ink-3">
          {rows.length} {rows.length === 1 ? "city" : "cities"} to work on
        </MonoMeta>
        {!open && (
          <MonoMeta className="min-w-0 truncate text-os-ink-3">
            first — {rows[0].title}
          </MonoMeta>
        )}
      </div>
      {open && (
        <ul data-testid="cities-next-up-list" className="mt-2 flex flex-col">
          {rows.map((row) => (
            <li key={row.slug} className="border-b border-os-border last:border-b-0">
              <button
                type="button"
                data-testid={`cities-next-up-row-${row.slug}`}
                onClick={canOpen ? () => onOpen(row.card) : undefined}
                className={cn(
                  "flex w-full items-baseline gap-3 py-1.5 text-left",
                  canOpen && "hover:underline hover:underline-offset-2",
                )}
              >
                <span className="w-11 shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-4">
                  {typeof row.priorityRank === "number" ? `#${row.priorityRank}` : "—"}
                </span>
                <span className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink">{row.title}</span>
                <span
                  data-testid={`cities-next-up-phase-${row.slug}`}
                  className="shrink-0 font-mono text-[0.6875rem] uppercase tracking-[0.06em] text-os-ink-3"
                >
                  {row.phaseLabel}
                </span>
                {row.boostReason ? (
                  <span
                    data-testid={`cities-next-up-boost-${row.slug}`}
                    title={row.boostReason}
                    className="hidden max-w-[16rem] truncate text-[0.6875rem] text-os-amber sm:inline"
                  >
                    ▲ {row.boostReason}
                  </span>
                ) : (
                  <span className="hidden shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-4 sm:inline">
                    {row.measuresDone}/{row.measuresTotal}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// ── The "what is up for being worked on" lane. Always rendered, whatever
//    the filters say — the empty case says so out loud instead of leaving
//    a blank space where the answer should be.
const WorkingNow = ({ items, totalWorking, hiddenByFilters, onOpen, canOpen }) => {
  if (items.length === 0) {
    return (
      <Card as="section" data-testid="cities-working-empty" className="px-5 py-4">
        <Label as="h2">Kit is on it — working right now</Label>
        <p data-testid="cities-working-empty-text" className="mt-1 text-[0.9375rem] leading-[1.5] text-os-ink-2">
          {hiddenByFilters
            ? `${totalWorking} ${totalWorking === 1 ? "city is" : "cities are"} being worked on right now, hidden by your filters.`
            : "0 cities are being worked on right now — the agents are idle. The next one up is named below."}
        </p>
      </Card>
    );
  }
  return (
    <section data-testid="cities-lane-working" className="flex flex-col gap-2">
      <h2 className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-4">
        Kit is on it — working right now
      </h2>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {items.map((item) => (
          <WorkingCard key={item.slug} item={item} onOpen={() => onOpen(item)} canOpen={canOpen} />
        ))}
      </div>
    </section>
  );
};

const CityDetail = ({ item, board, onClose }) => {
  const p = item.payload || {};
  const {
    title, stateWord, stateTone, progressLine, measures, blockedReason,
    videoUrl, videoId, completedAt, pageUrl, asOf, kitNow, inProgress,
  } = p;
  const left = MEASURES.filter(({ key }) => !measures?.[key]);
  const doneRows = MEASURES.filter(({ key }) => measures?.[key]);
  return (
    <DetailOverlay variant="panel" onClose={onClose} label={`${title} details`} testId="cities-detail">
      <div className="pr-12 pt-2">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="min-w-0 text-[1.25rem] font-semibold tracking-[-0.02em] text-os-ink">{title}</h2>
          <div className="flex shrink-0 items-center gap-2">
            {typeof p.priorityRank === "number" && (
              <span className="rounded-full border border-os-border px-2 py-0.5 font-mono text-[0.625rem] tracking-[0.04em] text-os-ink-3">
                #{p.priorityRank} in build queue
              </span>
            )}
            <StateWord tone={stateTone}>{stateWord}</StateWord>
          </div>
        </div>
        <p className="mt-2 text-[0.9375rem] leading-[1.5] text-os-ink-2">{progressLine}</p>
        {inProgress && kitNow && (
          <div data-testid="cities-detail-kitnow" className="mt-3 rounded-control border border-os-green bg-os-green/10 px-4 py-3">
            <p className="text-[0.9375rem] font-semibold text-os-green">● Kit is working on this right now</p>
            <p className="mt-0.5 text-[0.8125rem] text-os-ink-2">{kitNow}</p>
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-os-border pt-4">
        <Label as="h3">Completed parts</Label>
        {doneRows.length === 0 ? (
          <p className="mt-2 text-[0.875rem] text-os-ink-3">Nothing finished yet — build starts at the page itself.</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="cities-detail-done">
            {doneRows.map(({ key }) => (
              <span key={key} className="inline-flex items-center gap-1.5 rounded-full border border-os-green/40 bg-os-green/10 px-2.5 py-0.5 text-[0.75rem] font-medium text-os-green">
                ✓ {MEASURE_WORD[key]}
              </span>
            ))}
          </div>
        )}
      </div>

      {left.length > 0 && (
        <div className="mt-4 border-t border-os-border pt-4">
          <Label as="h3">Still to do</Label>
          <ol className="mt-2 flex flex-col gap-1.5" data-testid="cities-detail-todo">
            {left.map(({ key }, i) => (
              <li key={key} className="text-[0.875rem] leading-[1.5] text-os-ink-2">
                {i + 1}. {MEASURE_WORD[key]}
              </li>
            ))}
          </ol>
        </div>
      )}

      {blockedReason && (
        <div data-testid="cities-detail-blocker" className="mt-4 rounded-control border border-os-red bg-os-wash px-4 py-3">
          <Label as="h3" className="text-os-red">Blocker</Label>
          <p className="mt-1 text-[0.9375rem] leading-[1.5] text-os-ink">{blockedReason}</p>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-1.5">
        {completedAt && <MonoMeta as="p">Completed {completedAt}</MonoMeta>}
        {videoUrl && (
          <p className="text-[0.9375rem]">
            <a href={videoUrl} target="_blank" rel="noreferrer" className="text-os-ink underline decoration-os-border hover:decoration-os-ink">
              City video
            </a>
            {videoId && <span className="ml-2 font-mono text-[0.75rem] text-os-ink-3">ID: {videoId}</span>}
          </p>
        )}
        {pageUrl && (
          <p className="text-[0.9375rem]">
            <a href={pageUrl} target="_blank" rel="noreferrer" className="text-os-ink underline decoration-os-border hover:decoration-os-ink">
              Live city page
            </a>
          </p>
        )}
      </div>

      <p className="mt-5 font-mono text-[0.8125rem] text-os-ink-3">Current as of {asOf}</p>
      {board?.live && (
        <div className="mt-4 border-t border-os-border pt-4">
          <NoteOnCard board={board} slug={item.slug} testId={`cities-note-${item.slug}`} />
        </div>
      )}
      <ItemEditorBar board={board} item={item} onClose={onClose} testId={`cities-editor-${item.slug}`} />
    </DetailOverlay>
  );
};

export const CitiesBoard = ({ items, meta, variant = "desktop", board }) => {
  // Deep link (Jeff 2026-11-03: task's board link didn't work): /tjb/cities?city=<slug>
  // opens that city's detail panel on load. Also the canonical way task
  // descriptions point at a specific city (deep link + search term, together).
  const [openSlug, setOpenSlug] = useState(() => {
    try {
      const c = new URLSearchParams(window.location.search).get("city");
      return c && items.some((i) => i.slug === c) ? c : null;
    } catch {
      return null;
    }
  });
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("");
  const [measure, setMeasure] = useState("");
  const [laneFilter, setLaneFilter] = useState("all");
  const [density, setDensity] = useState("comfortable");
  const [showDoneList, setShowDoneList] = useState(false);
  const [queueLimit, setQueueLimit] = useState(QUEUE_PAGE);

  // A deep link for a city not yet in `items` (live data arriving late)
  // resolves on the next fetch; items refreshes clear the URL back.
  useEffect(() => {
    if (openSlug && !items.some((i) => i.slug === openSlug)) {
      const c = new URLSearchParams(window.location.search).get("city");
      if (c && c === openSlug) return; // still pending in a later fetch
      setOpenSlug(null);
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("city");
        window.history.replaceState(null, "", u);
      } catch {
        /* no-op */
      }
    }
  }, [items, openSlug]);
  const desktop = variant === "desktop";

  const openItem = useMemo(() => items.find((i) => i.slug === openSlug), [items, openSlug]);
  const close = useCallback(() => setOpenSlug(null), []);
  const openCity = useCallback((item) => setOpenSlug(item.slug), []);
  // Signed-out (baked) mode has no live row behind a card, so nothing is
  // clickable there — no dead clicks, no cursor promising an action that
  // cannot happen.
  const canOpen = Boolean(board?.live);

  const lanes = useMemo(() => buildLanes(items), [items]);
  const areas = useMemo(() => areaCounts(items), [items]);

  const filtered = useMemo(() => {
    const out = {};
    for (const [lane, list] of Object.entries(lanes)) {
      out[lane] = list.filter((item) => matchesFilters(item, { query, area, measure }));
    }
    return out;
  }, [lanes, query, area, measure]);

  // The next-up strip (Jeff directive 2026-10-23): the five cities the new
  // order says come next — partway-done pages worth finishing first, then
  // new pages by value, with a boosted city (multi-market provider) at
  // the top. It reads the producer's stamp first, then falls back to the
  // board's own cards. It respects the filters like the "next up" card
  // does: the board never names a city it is not showing. A deeper pool
  // is drawn so filtering one city away still leaves five on screen.
  const nextUpRows = useMemo(() => {
    const pool = nextUpList(items, meta, 25);
    const visible = pool.filter((row) => matchesFilters(row.card, { query, area, measure }));
    return visible.slice(0, 5);
  }, [items, meta, query, area, measure]);

  // The next city up — the answer to "which page is up for being worked
  // on" when Kit has nothing in flight. It respects the filters: the board
  // never names a city that is hidden from view.
  //
  // 2026-10-23: it is now the HEAD of the next-up strip, so the one-line
  // answer and the strip can never name different cities — the board
  // speaks with one voice about what comes next.
  const upNext = useMemo(() => {
    const head = nextUpRows[0];
    if (head) return head.card;
    // No strip row (every candidate is filtered out) — fall back to the
    // lane order so the board still names a city that is on screen.
    return nextUp(filtered);
  }, [nextUpRows, filtered]);

  const hiddenCount = useMemo(
    () => (items.length || 0) - Object.values(filtered).reduce((n, l) => n + l.length, 0),
    [items.length, filtered],
  );

  const filtersOn = Boolean(query.trim() || area || measure);
  const clearFilters = () => { setQuery(""); setArea(""); setMeasure(""); setQueueLimit(QUEUE_PAGE); };
  const inLane = (lane) => laneFilter === "all" || laneFilter === lane;

  const gridClass = desktop
    ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
    : "flex flex-col gap-3";

  const cardFor = (item) =>
    laneOf(item) === "working"
      ? <WorkingCard key={item.slug} item={item} onOpen={() => openCity(item)} canOpen={canOpen} />
      : <CityCard key={item.slug} item={item} onOpen={() => openCity(item)} density={density} canOpen={canOpen} />;

  const queued = filtered.queued || [];
  const queueShown = queued.slice(0, queueLimit);
  const laneCount = (lane) => (filtered[lane] || []).length;

  return (
    <div data-testid="cities-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <SummaryBlock total={items.length} lanes={lanes} meta={meta} live={board?.live} desktop={desktop} />

        {/* The next-up strip — the new board order in five rows, above the
            card grid. Says what to work on next, and why. */}
        <NextUpStrip rows={nextUpRows} onOpen={openCity} canOpen={canOpen} />

        {/* The "up for being worked on" answer — always on the page, even at zero. */}
        {upNext && !lanes.working.length && (
          <Card as="section" data-testid="cities-next-up" className="px-5 py-3.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Label as="h2">Next up for Kit</Label>
              <button
                type="button"
                data-testid="cities-next-up-open"
                onClick={canOpen ? () => openCity(upNext) : undefined}
                className="min-h-[1.75rem] text-[0.9375rem] font-semibold text-os-ink underline-offset-4 hover:underline"
              >
                {upNext.payload?.title}
              </button>
              <MonoMeta className="text-os-ink-3">
                {upNext.payload?.priorityRank ? `priority #${upNext.payload.priorityRank} · ` : ""}
                {measuresDone(upNext)}/{measuresTotal(upNext)} parts done
              </MonoMeta>
            </div>
          </Card>
        )}

        {/* Filters. */}
        <div
          data-testid="cities-filters"
          className="flex flex-wrap items-center gap-2 rounded-card border border-os-border bg-white px-4 py-2.5"
        >
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a city or a state…"
            aria-label="Search cities"
            data-testid="cities-filter-search"
            className="w-56 rounded-control border border-os-border bg-transparent px-3 py-1.5 text-[0.875rem] placeholder:text-os-ink-4 focus:border-os-border-strong focus:outline-none"
          />
          <select
            value={area}
            onChange={(e) => setArea(e.target.value)}
            aria-label="Filter by state"
            data-testid="cities-filter-area"
            className="rounded-control border border-os-border bg-transparent px-3 py-1.5 text-[0.8125rem] focus:outline-none"
          >
            <option value="">All states</option>
            {areas.map((a) => (
              <option key={a.key} value={a.key}>{a.key} ({a.count})</option>
            ))}
          </select>
          <select
            value={measure}
            onChange={(e) => setMeasure(e.target.value)}
            aria-label="Filter by missing part"
            data-testid="cities-filter-measure"
            className="rounded-control border border-os-border bg-transparent px-3 py-1.5 text-[0.8125rem] focus:outline-none"
          >
            <option value="">Any part</option>
            {MEASURES.map(({ key }) => (
              <option key={key} value={key}>Missing {MEASURE_WORD[key].toLowerCase()}</option>
            ))}
          </select>

          <div className="ml-auto flex flex-wrap items-center gap-1.5" data-testid="cities-lane-filter">
            <WorkingStatus count={laneCount("working")} />
            {LANE_FILTERS.map((lane) => (
              <LaneFilter
                key={lane}
                lane={lane}
                count={lane === "all" ? items.length : laneCount(lane)}
                active={laneFilter === lane}
                onClick={() => setLaneFilter(lane)}
              />
            ))}
          </div>

          <div className="flex items-center gap-1" data-testid="cities-density">
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">Rows</span>
            {["comfortable", "compact"].map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`cities-density-${d}`}
                aria-pressed={density === d}
                onClick={() => setDensity(d)}
                className={cn(
                  "min-h-[1.75rem] rounded-control border px-2 py-1 font-mono text-[0.6875rem]",
                  density === d
                    ? "border-os-border-strong bg-os-ink text-white"
                    : "border-os-border text-os-ink-3 hover:text-os-ink",
                )}
              >
                {d}
              </button>
            ))}
          </div>

          {filtersOn && (
            <button
              type="button"
              data-testid="cities-filter-clear"
              onClick={clearFilters}
              className="min-h-[1.75rem] font-mono text-[0.6875rem] text-os-ink-3 underline underline-offset-4 hover:text-os-ink"
            >
              Clear filters
            </button>
          )}
        </div>

        {filtersOn && (
          <p data-testid="cities-filter-count" className="px-1 font-mono text-[0.6875rem] text-os-ink-3">
            {hiddenCount > 0
              ? `${hiddenCount} of ${items.length} cities are hidden by the filters`
              : `all ${items.length} cities match`}
          </p>
        )}

        {/* 1. Working now — always rendered, empty state says so out loud. */}
        <WorkingNow
          items={filtered.working || []}
          totalWorking={lanes.working.length}
          hiddenByFilters={lanes.working.length > 0 && (filtered.working || []).length === 0}
          onOpen={openCity}
          canOpen={canOpen}
        />

        {/* 2. Blocked. */}
        {inLane("blocked") && laneCount("blocked") > 0 && (
          <Lane lane="blocked" items={filtered.blocked} onOpen={openCity} defaultOpen>
            <div className={gridClass}>{(filtered.blocked || []).map(cardFor)}</div>
          </Lane>
        )}

        {/* 3. In build — part of the page is done, the rest isn't. */}
        {inLane("building") && laneCount("building") > 0 && (
          <Lane lane="building" items={filtered.building} onOpen={openCity} defaultOpen>
            <div className={gridClass}>{(filtered.building || []).map(cardFor)}</div>
          </Lane>
        )}

        {/* 4. Queued — a prioritized list, not a card wall. */}
        {inLane("queued") && queued.length > 0 && (
          <Lane lane="queued" items={queued} onOpen={openCity} defaultOpen>
            <ul data-testid="cities-queue-list" className="rounded-card border border-os-border bg-white px-4">
              {queueShown.map((item) => (
                <QueueRow key={item.slug} item={item} onOpen={() => openCity(item)} canOpen={canOpen} />
              ))}
            </ul>
            {queued.length > queueShown.length && (
              <button
                type="button"
                data-testid="cities-queue-more"
                onClick={() => setQueueLimit((n) => n + QUEUE_PAGE)}
                className="min-h-[1.75rem] self-start font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
              >
                show {Math.min(QUEUE_PAGE, queued.length - queueShown.length)} more of {queued.length}
              </button>
            )}
          </Lane>
        )}

        {/* 5. Completed — a summary, with the full list one click behind it. */}
        {inLane("done") && (
          <CompletedLane
            doneItems={filtered.done || []}
            onOpen={openCity}
            showingAll={showDoneList}
            onShowAll={() => setShowDoneList((s) => !s)}
            canOpen={canOpen}
          />
        )}

        {/* Set aside — Jeff's own parked cities. */}
        {inLane(ARCHIVED_LANE) && laneCount(ARCHIVED_LANE) > 0 && (
          <Lane lane={ARCHIVED_LANE} items={filtered.archived} onOpen={openCity} defaultOpen>
            <div className={gridClass}>{(filtered.archived || []).map(cardFor)}</div>
          </Lane>
        )}

        {/* Anything the producer marks with a state the board doesn't know. */}
        {inLane(OTHER_LANE) && laneCount(OTHER_LANE) > 0 && (
          <Lane lane={OTHER_LANE} items={filtered.other} onOpen={openCity} defaultOpen>
            <div className={gridClass}>{(filtered.other || []).map(cardFor)}</div>
          </Lane>
        )}

        {filtersOn && Object.values(filtered).every((l) => l.length === 0) && (
          <Card quiet className="px-5 py-4">
            <p data-testid="cities-no-match" className="text-[0.875rem] text-os-ink-2">
              No cities match those filters. Clear them to see the whole board again.
            </p>
          </Card>
        )}
      </div>
      {openItem && board?.live && <CityDetail item={openItem} board={board} onClose={close} />}
    </div>
  );
};

export default CitiesBoard;
